import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { Form, Formik } from 'formik';

import { Button, ClickableIcon, ContainerHeader, ErrorResponse, Toolbar } from '~components';
import { FieldProps, Fields } from '~components/form-fields';
import { faArrowLeft, faPlus } from '~components/icon';
import Modal from '~components/Modal';

import { useToast } from '~libs/toast';

import { ClientBudgetStatus } from '~support-at-home/models';

import editBudgetFields from '~support-at-home/forms/budgets/editBudget';

import { getAll as getClients } from '~main/actions/clients';
import { getClientPackageSchedules } from '~main/actions/clients/clientPackageSchedules';
import {
  useGetBudgetQuery,
  useGetFilteredServicesQuery,
  useSaveBudgetMutation,
} from '~support-at-home/actions/budgets';
import { getAll as getAllClientBudgetStatuses } from '~support-at-home/actions/clientBudgetStatuses';
import { getAll as getPricePlanRevisions } from '~support-at-home/actions/pricePlanRevisions';
import { getAll as getPricePlans } from '~support-at-home/actions/pricePlans';
import { getAll as getSahBudgets } from '~support-at-home/actions/sahBudgets';

import { selectClientBudgetStatuses } from '~support-at-home/selectors/clientBudgetStatuses';

import BudgetActivityLog from './BudgetActivityLog';
import BudgetSummary from './BudgetSummary';

const BudgetEdit: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToast();
  const budgetStatuses = useAppSelector(selectClientBudgetStatuses);

  const { data: budget } = useGetBudgetQuery(id ? { id } : skipToken);

  const activeStatus = useMemo(() => {
    return budgetStatuses.find((status: ClientBudgetStatus) => status?.tag?.toLowerCase() === 'active');
  }, [budgetStatuses]);

  useEffect(() => {
    dispatch(getClients());
    dispatch(getPricePlans());
    dispatch(getAllClientBudgetStatuses());
  }, []);

  const {
    client_id,
    client_sah_budget_id,
    price_plan_revision_id,
    status_id,
    client_package_id,
    start_date,
    end_date,
    total_amount,
    available_amount,
    sections,
    grand_total,
    price_plan_revision,
    total_includes_case_management,
    supplements,
    ignore_sah_filters,
  } = budget || {};

  const price_plan_id = price_plan_revision?.plan_id;
  const isPlanDeleted = price_plan_revision?.price_plan?.is_deleted === true;
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  useEffect(() => {
    if (price_plan_id) {
      dispatch(getPricePlanRevisions({ plan_id: price_plan_id }));
    }
  }, [price_plan_id]);

  useEffect(() => {
    if (client_id && end_date) {
      dispatch(getClientPackageSchedules({ client_id, date: end_date }));
    }
  }, [client_id, end_date]);

  useEffect(() => {
    if (client_id) {
      dispatch(getSahBudgets({ client_id }));
    }
  }, [client_id]);

  useGetFilteredServicesQuery(
    price_plan_id
      ? {
          revision_id: price_plan_revision_id ?? '',
          client_id,
          package_fixed_id: client_package_id,
          ignore_sah_filters: ignore_sah_filters ?? false,
        }
      : skipToken,
  );

  const [saveBudget] = useSaveBudgetMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const submit = async (
    setSubmitting: (isSubmitting: boolean) => void,
    values: Record<string, any | null | undefined>,
  ) => {
    try {
      setSubmitting(true);
      setSubmitError(null);

      // Strip non-API fields
      const { package_fixed_id, grand_total, initial_budget, price_plan_deleted, available_amount, ...payload } =
        values;

      const result = await saveBudget({ id, client_id, client_package_id, ...payload }).unwrap();

      if (result.id) {
        showToast('success', 'Budget updated successfully');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err: any) {
      const errorMessage = err?.data?.message ?? 'An unknown error occurred while saving the budget.';
      setSubmitError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (submitError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submitError]);

  const onBack = () => navigate(`/support-at-home/budgets`);

  const fieldDefinitions = useMemo(
    () => Object.entries(editBudgetFields as FieldProps).map(([key, props]) => ({ key, ...props })),
    [editBudgetFields],
  );

  if (!id) return <></>;

  return (
    <div className="p-2 budget">
      <ContainerHeader icon={faPlus} iconSize="1x" className="info" dataCy="edit-budget-header">
        Edit Budget - {budget?.reference}
      </ContainerHeader>
      <Toolbar
        id="budget-edit-toolbar"
        leftCluster={
          <ClickableIcon
            icon={faArrowLeft}
            id="budget-edit-back-button"
            onClick={onBack}
            toolTip="Back to Budgets"
          />
        }
      />
      <Formik
        enableReinitialize
        initialValues={{
          id,
          client_id: client_id ?? '',
          client_sah_budget_id: client_sah_budget_id ?? '',
          package_fixed_id: client_package_id ?? 0,
          price_plan_revision_id: price_plan_revision_id ?? '',
          status_id: status_id ?? '',
          start_date: start_date ?? '',
          end_date: end_date ?? '',
          total_amount: total_amount ?? 0,
          available_amount: available_amount ?? 0,
          sections: sections ?? [],
          grand_total: grand_total ?? {},
          ...(total_includes_case_management != null && { total_includes_case_management }),
          ignore_sah_filters: ignore_sah_filters ?? false,
          price_plan_deleted: isPlanDeleted,
          initial_budget: budget ? JSON.parse(JSON.stringify(budget)) : {},
          supplements: supplements ?? [],
        }}
        onSubmit={(values, { setSubmitting }) => submit(setSubmitting, values)}
      >
        {({ values, setFieldValue, setFieldTouched, touched, isSubmitting }) => {
          useEffect(() => {
            const handleStatusUpdate = async () => {
              if (!touched.status_id) return;

              const newStatusId = values.status_id;

              if (activeStatus && newStatusId === activeStatus.id) {
                setStatusModalOpen(true);
              }
            };
            handleStatusUpdate();
          }, [values.status_id]);

          return (
            <Form
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault();
              }}
            >
              <div className="mt-4">
                <BudgetSummary
                  budgetInfo={{
                    ...budget,
                    price_plan_revision_id: values.price_plan_revision_id,
                    status_id: values.status_id,
                    start_date: values.start_date,
                    end_date: values.end_date,
                  }}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                />
                <hr className="section-split-line" />
              </div>
              <div ref={errorRef}>
                <ErrorResponse message={submitError} />
              </div>
              <Fields
                definitions={fieldDefinitions}
                row={budget}
                values={values}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                touched={touched}
              />
              <div className="d-flex justify-content-between mt-4 mb-4">
                <Button
                  size="sm"
                  disabled={isSubmitting}
                  className="ms-4 me-4 ps-4 pe-4"
                  type="button"
                  onClick={onBack}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={isSubmitting || isPlanDeleted}
                  className="ms-4 me-4 ps-4 pe-4"
                  color="success"
                  type="submit"
                >
                  Update Budget
                </Button>
              </div>
              <Modal
                isOpen={statusModalOpen}
                setIsOpen={setStatusModalOpen}
                headerClassName="bg-warning"
                header="Caution"
              >
                <div className="mb-4">
                  There can only be 1 <b>active</b> budget within the same quarter per funding type, per client.
                </div>
                <div>
                  Any conflicting budgets will be set to <b>inactive</b>.
                </div>
              </Modal>
            </Form>
          );
        }}
      </Formik>
      <BudgetActivityLog id={id} />
    </div>
  );
};

export default BudgetEdit;
